import { Box, IconButton } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import InfoIcon from '@mui/icons-material/Info'
import DoneIcon from '@mui/icons-material/Done'
import { useEffect, useMemo, useRef, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import FinishJobDialog from '../FinishJobDialog'
import { DeliveryDeviationCell, DeliveryStatusCell, ExpectedEndCell, FabricationOrderCell, RequiredDateCell } from '../DeliveryCells'
import DeliveryStatusCounters from '../DeliveryStatusCounters'
import DeliveryCountersError from '../DeliveryCountersError'
import DeliveryListError from '../DeliveryListError'
import DeliveryDashboardLink from '../DeliveryDashboardLink'
import useDeliveries from '../useDeliveries'
import { requiredDateCsv } from '../deliveryStatusPresentation'
import PlannerDetailsTable from './PlannerDetailsTable'
import buildPlannerDetailsCells from './plannerDetailsCells'
import { finishAvailability, isAllowStartAnyJob } from './finishAvailability'
import SequenceMoveContext from './SequenceMoveContext'
import SequenceRowMenu from './SequenceRowMenu'
import SequenceToolbarControls from './SequenceToolbarControls'
import useResequencePreviewPreference from './useResequencePreviewPreference'
import useSequenceArrowsPreference from './useSequenceArrowsPreference'
import useSequenceMove from './useSequenceMove'
import { asksDeliverySort, lastPageWithDataIfOutOfRange, withDeliverySortKeys } from './plannerDetailsRequest'

const ALL_MACHINES = 'all'
const TUNE_KEY = 'plannerDetails'
// D-R -- the order each tab starts in and goes back to on "Remove sort". The key of a machine's queue is
// Sun's virtual `position` (contract §1); all machines go by start date; a plan by part keeps today's
// stored queue number.
const MACHINE_SORT = { field: 'position', direction: 1 }
const ALL_MACHINES_SORT = { field: 'startDate', direction: 1 }
const PART_PLAN_SORT = { field: 'order', direction: 1 }

// RN39 / contract §7 -- the four delivery columns are filtered by the counters, not by a column filter nor the
// generic search. `searchable: false` + `disableGenericSearch: true` are the frozen flags; `search: false` is
// what makes the table leave the header filter cell empty for a SORTABLE column (without it,
// `searchable: false` falls back to the sortable rule and keeps the filter).
const NO_COLUMN_FILTER = { searchable: false, search: false, disableGenericSearch: true }

/**
 * The 17 columns the details table declares (story 175 included), in the order the saved TUNEs know.
 * @param {Function} t_
 * @param {Boolean} isJobMode The plan is by part (criterion 'Job'): the part column shows
 */
const buildColumns = (t_, isJobMode) => [
  { field: 'device', name: t_('Asset'), readOnly: true },
  { field: 'startDate', name: t_('Start'), type: 'datetime', readOnly: true },
  { field: 'endDate', name: t_('End'), type: 'datetime', readOnly: true },
  { field: 'duration', name: t_('Duration'), readOnly: true },
  // Story 175 (S116, RN31): a forecast order is told apart in this same column, not in a new one.
  { field: 'fabricationOrder', name: t_('FO'), readOnly: true, render: (row) => <FabricationOrderCell row={row} /> },
  { field: 'job', name: t_('Job (step n./total steps)'), readOnly: true },
  // Sun cannot sort by operators (a collection) and leaves them out of the general search, but they
  // can still be filtered by column.
  { field: 'operators', name: t_('Operators'), type: 'smartMultiSelect', isSimpleData: true, edit: { field: 'workerNameSurname' }, readOnly: true, sortable: false, searchable: true, disableGenericSearch: true },
  { field: 'partReference', name: t_('Part Reference'), readOnly: true },
  // Story 177 (S98, RN41, D-M): the reference's description, right after the reference. Sun sorts, filters and
  // searches it by this same field (S99); a reference with no description leaves the cell empty (S101).
  { field: 'partReferenceDescription', name: t_('Part Reference Description'), readOnly: true },
  { field: 'part', name: t_('Part name'), hide: !isJobMode, readOnly: true },
  { field: 'client', name: t_('Client'), readOnly: true },
  { field: 'toolReferences', name: t_('Tool reference'), type: 'smartMultiSelect', isSimpleData: true, readOnly: true },
  // Story 96 (CA5/RN12/P6): the job's MOS queue priority, read only; a plain scalar, appended after the
  // columns that were there before the delivery ones.
  { field: 'jobPriority', name: t_('Job priority'), readOnly: true },
  // Story 175 (CU8, RN29, contract §7) -- the delivery of the job's fabrication order, always LAST so a user
  // without a saved tune sees it at the end (the tune merges by `field`, RN40). DL-1 (b): "Required date" is
  // NOT a new column but the existing `field: 'deliveryDate'` (that field IS the order's target date),
  // relabelled and moved here; its `field` stays so every saved tune keeps finding it (S131).
  { field: 'deliveryDate', name: t_('Required date'), readOnly: true, ...NO_COLUMN_FILTER, render: (row) => <RequiredDateCell row={row} />, csvValue: requiredDateCsv },
  { field: 'expectedEndDate', name: t_('Expected end'), readOnly: true, ...NO_COLUMN_FILTER, render: (row) => <ExpectedEndCell row={row} /> },
  { field: 'deliveryDeviationDays', name: t_('Delivery deviation'), readOnly: true, ...NO_COLUMN_FILTER, render: (row) => <DeliveryDeviationCell row={row} /> },
  // RN38 / S-c -- the status is a filter (the counters), not an order.
  { field: 'deliveryStatus', name: t_('Delivery status'), readOnly: true, ...NO_COLUMN_FILTER, sortable: false, render: (row) => <DeliveryStatusCell row={row} /> }
]

const buildImportBeans = (t_) => [
  {
    title: t_('IMPORT FINISHED OFs'),
    name: t_('FABRICATION ORDER'),
    importType: 'UPDATE',
    bean: {
      beanName: 'FinishedFabricationOrderImportDto',
      beanPackage: 'com.smartpm.sun.usecase.finishedfabricationorderimport.infrastructure.rest',
      serviceName: 'FinishedFabricationOrderImportService',
      servicePackage: 'com.smartpm.sun.usecase.finishedfabricationorderimport.application'
    }
  }
]

/**
 * The details block of the Planner with the new table: what PlannerDetails did (story 175 included),
 * drawn after the machine queue prototype. One tab per machine (and one for all of them) instead of the
 * "Asset" selector in plans by fabrication order, each with its number of jobs; a machine tab shows each
 * job's position in its queue. Plans by part have no tabs, as they had no selector. Some cells are drawn
 * after the prototype too (see plannerDetailsCells).
 *
 * Above the table, the delivery counters, their error and the list's delivery error; below it, the link to
 * the deliveries dashboard. A click on a row selects its fabrication order in the Planner.
 *
 * @param {Object} props
 * @param {Object} props.selectedFile The plan, with `id` and `unitCriteria.name`
 * @param {Function} props.setEventInfoOnClick Opens a job's detail
 * @param {Function} props.reloadCalendar Called after finishing a job and after a job is moved
 * @param {Function} props.setOrderEdited Called with `true` after a job is moved (the plan's order changed, RN17)
 * @param {Function} props.showAlert Tells the user a move could not be done
 * @param {Object} props.plannerFilters The Timeline filters
 * @param {Number} props.metricsVersion The Planner's "the plan changed" signal: every change reloads the list and the counters
 * @param {Number} props.selectedFabricationOrderId The fabrication order selected in the Planner; its rows are highlighted
 * @param {Function} props.onFabricationOrderClick Called with a row's fabrication order id when the row is clicked
 */
const PlannerDetailsPanel = ({ selectedFile, setEventInfoOnClick, reloadCalendar = () => {}, setOrderEdited = () => {}, showAlert = () => {}, plannerFilters, metricsVersion = 0, selectedFabricationOrderId, onFabricationOrderClick = () => {} }) => {
  const { t_ } = useSmartTranslation()
  // The machine tabs of a plan, as Sun answers them (contract §2), kept with the plan they belong to so that
  // another plan never shows them while its own come.
  const [machinesOfPlan, setMachinesOfPlan] = useState({ planId: '', list: [] })
  // The open tab, kept with its plan too: a new plan opens "All machines" (S90) without a request for the
  // old tab on the new plan.
  const [openTab, setOpenTab] = useState({ planId: '', tab: ALL_MACHINES })
  // Contract §8 (H-9): the `totalElements` of the last list answer of each tab, by `<plan>|<tab>`.
  const [listTotals, setListTotals] = useState({})
  const [finishJobInfo, setFinishJobInfo] = useState({ open: false })
  const tableRowsRef = useRef([])
  const isJobMode = selectedFile?.unitCriteria?.name === 'Job'
  const planId = selectedFile?.id
  const machines = machinesOfPlan.planId === planId && !isJobMode ? machinesOfPlan.list : []
  // A tab Sun no longer lists (a machine hidden or emptied since) is not open any more: S88.
  const wantedTab = openTab.planId === planId ? openTab.tab : ALL_MACHINES
  const selectedTab = machines.some((machine) => machine.id === wantedTab) ? wantedTab : ALL_MACHINES
  const setSelectedTab = (tab) => setOpenTab({ planId, tab })
  const onMachineTab = !isJobMode && selectedTab !== ALL_MACHINES

  // Story 177, moving a job. The two preferences are the user's, kept in Sun (contract §6) and read once for the
  // panel; the move itself (field, arrows, menu, preview) lives in `useSequenceMove`.
  const isUserAuthorized = useAuthorization()
  const canOrder = isUserAuthorized('PLANNER_DETAIL_ORDER')
  const [previewOn, togglePreview] = useResequencePreviewPreference({ enabled: !isJobMode })
  const [arrowsVisible, toggleArrows] = useSequenceArrowsPreference({ enabled: !isJobMode })
  // Asked after every move, whatever Sun answered, without waiting for the Planner's own signal.
  const [tabsSignal, setTabsSignal] = useState(0)
  const jobCount = machines.find((machine) => machine.id === selectedTab)?.jobCount ?? 0
  const move = useSequenceMove({
    planId,
    deviceId: onMachineTab ? selectedTab : 0,
    jobCount,
    canOrder,
    previewOn,
    t_,
    reloadCalendar,
    setOrderEdited,
    showAlert,
    reloadTabs: () => setTabsSignal((signal) => signal + 1)
  })
  // D-X: with a position field open or a move being saved the tabs are not asked again; what came meanwhile
  // is asked once, with the latest, when that ends.
  const tabsKeyRef = useRef('')
  if (!move.paused) tabsKeyRef.current = metricsVersion + '|' + tabsSignal
  const tabsKey = tabsKeyRef.current

  // Story 175 (CU8, RN32/RN33) -- the counters over the table: the plan's global filters only, never what the
  // table itself does (tab, status filter, search, page, sort). This panel is only mounted while its
  // accordion block is expanded, so nothing is asked while it is folded (RN52).
  const deliveries = useDeliveries({ planificationId: planId, plannerFilters, metricsVersion })
  const retryDeliveries = deliveries.reload
  // CU9 / RN34-RN36 -- the ONE delivery status the table is filtered by (null = all, the value
  // `DeliveryStatusCounters` reads). Screen-session state: it survives plan, global filter, page and sort
  // changes while this block stays mounted. Never saved in the tune nor in any preference (DL-7).
  const [deliveryStatus, setDeliveryStatus] = useState(null)
  // S121 -- a new status filter starts on the first page. The table owns the page number, so the first
  // request made for the new status is sent with page 0 and the table follows Sun's answer.
  const pendingFirstPageRef = useRef({})
  const applyDeliveryStatus = (nextStatus) => {
    pendingFirstPageRef.current = { status: nextStatus }
    setDeliveryStatus(nextStatus)
  }
  // S122 -- pressing the active counter clears it; pressing another one replaces it (one status at a time).
  const toggleDeliveryStatus = (status) => applyDeliveryStatus(deliveryStatus === status ? null : status)
  const clearDeliveryStatus = () => applyDeliveryStatus(null)
  // S135 -- the list failed because of a delivery filter and/or sort: which one, and Sun's error ({} = no error).
  const [listError, setListError] = useState({})
  // S135 -- "Clear sort": the table goes back to the default order of the open tab on this signal.
  const [resetSortSignal, setResetSortSignal] = useState(0)
  const clearDeliverySort = () => setResetSortSignal((signal) => signal + 1)

  const columns = useMemo(() => buildColumns(t_, isJobMode), [t_, isJobMode])
  const importFile = useMemo(() => buildImportBeans(t_), [t_])
  const columnOverrides = useMemo(() => buildPlannerDetailsCells({ onMachineTab }), [onMachineTab])
  const defaultSort = isJobMode ? PART_PLAN_SORT : onMachineTab ? MACHINE_SORT : ALL_MACHINES_SORT

  // The tabs come from ONE request, asked when the plan changes and after every write of the plan
  // (`metricsVersion`), never because of a filter, a search or a refresh (S86). Plans by part have no tabs and
  // ask nothing. A failure leaves only "All machines", with no notice, until the next time they are asked (S88).
  useEffect(() => {
    if (!planId || isJobMode) return
    let current = true
    const loadMachines = async () => {
      let answer = []
      try {
        answer = await apiGet('plannerDetails/' + planId + '/machines')
      } catch (error) {
        answer = []
      }
      if (!current) return
      const list = (Array.isArray(answer) ? answer : []).map((tab) => ({ id: tab.deviceId, name: String(tab.deviceName ?? ''), jobCount: tab.jobCount }))
      setMachinesOfPlan({ planId, list })
    }
    loadMachines()
    return () => { current = false }
  }, [planId, isJobMode, tabsKey])

  // Story 195: with "Allow start any job" on, the previous job no longer blocks Finish (read once per login).
  const [frontEndConfig] = useFrontEndConfig()
  const allowStartAnyJob = isAllowStartAnyJob(frontEndConfig)
  const getFinishAvailability = (row) => finishAvailability(row, tableRowsRef.current, isJobMode, allowStartAnyJob)

  const getDetailData = async (row) => {
    const response = await apiPost('plannerDetail', row)
    setEventInfoOnClick(response)
  }

  // S130 / RN41 / RN45 -- a row click toggles the ONE selected fabrication order of the Planner (the same
  // toggle the machine drawer and the Gantt use); it neither opens the job detail, nor scrolls the Gantt, nor
  // filters anything. The rows of the selected order are highlighted wherever they are.
  // A press on any button of the row (Finish, Details) is not a row selection: the origin of each click is
  // noted on its way down (capture phase, before the row sees it) and read when the row handles it.
  const clickStartedOnButtonRef = useRef(false)
  const noteClickOrigin = (event) => {
    clickStartedOnButtonRef.current = Boolean(event.target?.closest?.('button'))
  }
  const selectFabricationOrderOfRow = (row) => {
    if (clickStartedOnButtonRef.current) {
      return
    }
    onFabricationOrderClick(row.fabricationOrderId)
  }
  // External data: the Planner hands a missing selection as null.
  const hasSelectedOrder = selectedFabricationOrderId !== null && selectedFabricationOrderId !== undefined
  const highlightSelectedFabricationOrder = (row) => (
    hasSelectedOrder && row.fabricationOrderId === selectedFabricationOrderId ? 'action.selected' : ''
  )
  const stopRowClick = (event) => event.stopPropagation()

  const actions = {
    custom: [
      {
        name: 'Finish',
        contextMenu: false,
        render: (row) => {
          const { disabled: finishDisabled, tooltipKey } = getFinishAvailability(row)
          return (
            <Tooltip title={tooltipKey ? t_(tooltipKey) : ''}>
              {/* S130: the row is clickable (FO selection); its action buttons must not reach it. */}
              <span onClick={stopRowClick}>
                <IconButton
                  disabled={finishDisabled}
                  onClick={() => {
                    if (!finishDisabled) {
                      setFinishJobInfo({ ...row, open: true })
                    }
                  }}
                  color={row.finished ? 'primary' : 'inherit'}
                >
                  <DoneIcon />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
      },
      {
        name: 'Details',
        contextMenu: false,
        render: (row) => (
          <IconButton onClick={(event) => {
            stopRowClick(event)
            getDetailData(row)
          }}
          >
            <InfoIcon />
          </IconButton>
        )
      }
    ]
  }

  // Contract §4 -- `deliveryStatus` travels as a SIBLING of `plannerFilters`, never inside it (the Gantt and
  // the planner engine share `PlannerFilters`), and only when there is a filter.
  const buildListRequest = (pagination) => {
    const withRequiredDateSort = withDeliverySortKeys(pagination)
    const queryBuilder = pendingFirstPageRef.current.status !== undefined ? { ...withRequiredDateSort, page: 0 } : withRequiredDateSort
    const request = { queryBuilder, plannerFilters }
    if (deliveryStatus !== null) {
      request.deliveryStatus = deliveryStatus
    }
    return request
  }

  const dataFetch = async (pagination) => {
    const request = buildListRequest(pagination)
    const requestedTab = onMachineTab ? selectedTab : ALL_MACHINES
    const url = onMachineTab ? 'plannerDetails/' + planId + '/device/' + selectedTab : 'plannerDetails/' + planId
    let response
    try {
      response = await apiPost(url, request)
    } catch (error) {
      // RN56 -- only a request that asked about delivery gets the delivery error (and an empty page, never a
      // stale one). Any other failure goes on to the table, which keeps its rows and stops its refresh (H-15).
      const askedFilter = request.deliveryStatus !== undefined
      const askedSort = asksDeliverySort(request.queryBuilder)
      if (!askedFilter && !askedSort) {
        throw error
      }
      setListError({ error, filter: askedFilter, sort: askedSort })
      tableRowsRef.current = []
      return { content: [], totalElements: 0, number: 0, size: request.queryBuilder?.size }
    }
    response = await lastPageWithDataIfOutOfRange(apiPost, url, request, response)
    setListError({})
    if (pendingFirstPageRef.current.status !== undefined && pendingFirstPageRef.current.status === (request.deliveryStatus ?? null)) {
      pendingFirstPageRef.current = {}
    }
    tableRowsRef.current = Array.isArray(response?.content) ? response.content : []
    if (typeof response?.totalElements === 'number') {
      const totalKey = planId + '|' + requestedTab
      setListTotals((totals) => (totals[totalKey] === response.totalElements ? totals : { ...totals, [totalKey]: response.totalElements }))
    }
    return response
  }

  const tabs = isJobMode
    ? undefined
    : {
        value: selectedTab,
        onChange: setSelectedTab,
        items: [
          // §2 does not count "All machines": its tab has no number; the summary next to the pager has it.
          { value: ALL_MACHINES, label: t_('All machines') },
          ...machines.map((machine) => ({ value: machine.id, label: machine.name, count: machine.jobCount }))
        ]
      }

  // H-9: the jobs the table shows with its search and filters (the last answer of the list), not the tab's number.
  const shownCount = listTotals[planId + '|' + (onMachineTab ? selectedTab : ALL_MACHINES)]
  const tabName = onMachineTab ? machines.find((machine) => machine.id === selectedTab)?.name : t_('All machines')
  const jobsText = shownCount === undefined ? '' : t_('{{count}} jobs', { count: shownCount })
  const footerSummary = jobsText === '' || isJobMode ? jobsText : tabName + ' · ' + jobsText

  // What the position cells and the row menus read (they are drawn by the table). Outside a machine tab: nothing to move.
  const moveContext = onMachineTab
    ? { enabled: true, canOrder, arrowsVisible, previewOn, jobCount, ...move }
    : { enabled: false }
  // The preview switch and the arrows button, in the bar next to the search, only in a machine tab.
  const sequenceToolbar = { render: { fetchFunction: () => <SequenceToolbarControls previewOn={previewOn} onTogglePreview={togglePreview} arrowsVisible={arrowsVisible} onToggleArrows={toggleArrows} /> } }

  return (
    <>
      <FinishJobDialog
        open={finishJobInfo.open}
        onClose={() => { setFinishJobInfo({ ...finishJobInfo, open: false }) }}
        onDone={reloadCalendar}
        fabricationOrder={finishJobInfo?.fabricationOrder}
        jobReference={finishJobInfo?.job}
        duration={finishJobInfo?.duration}
        plannerDetailId={finishJobInfo?.id}
      />
      {deliveries.error
        ? <DeliveryCountersError error={deliveries.error} onRetry={retryDeliveries} filterActive={deliveryStatus !== null} onClearFilter={clearDeliveryStatus} />
        : null}
      {deliveries.data
        ? <DeliveryStatusCounters counts={deliveries.data.counts} activeStatus={deliveryStatus} onToggle={toggleDeliveryStatus} onClear={clearDeliveryStatus} />
        : null}
      {listError.error !== undefined
        ? <DeliveryListError listError={listError} onClearFilter={clearDeliveryStatus} onClearSort={clearDeliverySort} />
        : null}
      <Box onClickCapture={noteClickOrigin}>
        <SequenceMoveContext.Provider value={moveContext}>
          <PlannerDetailsTable
            tableTuneUniqueKey={TUNE_KEY}
            columns={columns}
            dataFetch={dataFetch}
            actions={actions}
            // RN43 / DL-18 (S159): every write of the plan (`metricsVersion`) reloads the list at once, keeping
            // the page, sort, status filter, tab and column filters the table already holds -- `forceLoad`
            // only asks for a fetch, it resets nothing. RN51 (S160): so does another plan, without waiting
            // for the table's next refresh.
            forceLoad={JSON.stringify({ planificationId: planId, plannerFilters, deliveryStatus, metricsVersion, tab: selectedTab })}
            importFile={importFile}
            tabs={tabs}
            pageResetKey={selectedTab}
            positionColumn={onMachineTab}
            footerSummary={footerSummary}
            columnOverrides={columnOverrides}
            defaultSort={defaultSort}
            onRowClick={selectFabricationOrderOfRow}
            rowBackgroundColorFunction={highlightSelectedFabricationOrder}
            resetSelectedRow={selectedFabricationOrderId}
            resetSort={resetSortSignal}
            refreshPaused={move.paused}
            toolbar={onMachineTab ? sequenceToolbar : undefined}
            rowMenu={(row) => <SequenceRowMenu row={row} onViewDetail={getDetailData} />}
          />
        </SequenceMoveContext.Provider>
      </Box>
      <DeliveryDashboardLink planificationId={planId} plannerFilters={plannerFilters} />
    </>
  )
}

export default PlannerDetailsPanel
